import { z } from "zod";
import { hexColorSchema } from "./common.ts";

export const EDITOR_SCHEMA_VERSION = 1;

/** Localized static text: language tag → text. The menu default language is the source. */
export const localizedTextSchema = z.record(z.string(), z.string().max(5000));
export type LocalizedText = z.infer<typeof localizedTextSchema>;

export const breakpointSchema = z.enum(["mobile", "tablet", "desktop"]);
export type Breakpoint = z.infer<typeof breakpointSchema>;
export const BREAKPOINT_WIDTHS: Record<Breakpoint, number> = { mobile: 390, tablet: 820, desktop: 1280 };
/** Mobile-first: tablet applies ≥ 640px, desktop ≥ 1024px. */
export const BREAKPOINT_MIN_WIDTH: Record<Breakpoint, number> = { mobile: 0, tablet: 640, desktop: 1024 };

const spacing = z.number().int().min(0).max(240);
const colorOrToken = z.union([hexColorSchema, z.enum(["primary", "background", "surface", "text", "muted", "accent", "transparent"])]);

/** Constrained style properties — no arbitrary CSS. */
export const blockStyleSchema = z
  .object({
    paddingTop: spacing,
    paddingRight: spacing,
    paddingBottom: spacing,
    paddingLeft: spacing,
    marginTop: spacing,
    marginBottom: spacing,
    background: colorOrToken,
    color: colorOrToken,
    borderWidth: z.number().int().min(0).max(16),
    borderColor: colorOrToken,
    borderRadius: z.number().int().min(0).max(64),
    textAlign: z.enum(["left", "center", "right"]),
    maxWidth: z.number().int().min(160).max(1600),
    fontSize: z.enum(["xs", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl"]),
    fontFamily: z.enum(["heading", "body"]),
    fontWeight: z.enum(["normal", "medium", "semibold", "bold"]),
    shadow: z.enum(["none", "sm", "md", "lg"]),
    hidden: z.boolean(),
  })
  .partial()
  .strict();
export type BlockStyle = z.infer<typeof blockStyleSchema>;

export type Block = {
  id: string;
  type: string;
  /** Props are validated by the per-type schema in the block registry. */
  props: Record<string, unknown>;
  style?: BlockStyle;
  /** Mobile-first overrides. */
  overrides?: Partial<Record<"tablet" | "desktop", { style?: BlockStyle; props?: Record<string, unknown> }>>;
  children?: Block[];
  hidden?: boolean;
  locked?: boolean;
  /** Version of the block's props schema. */
  v?: number;
};

export const blockSchema: z.ZodType<Block> = z.lazy(() =>
  z
    .object({
      id: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
      type: z.string().min(1).max(48).regex(/^[a-z0-9-]+$/),
      props: z.record(z.string(), z.unknown()),
      style: blockStyleSchema.optional(),
      overrides: z
        .object({
          tablet: z.object({ style: blockStyleSchema.optional(), props: z.record(z.string(), z.unknown()).optional() }).strict().optional(),
          desktop: z.object({ style: blockStyleSchema.optional(), props: z.record(z.string(), z.unknown()).optional() }).strict().optional(),
        })
        .strict()
        .optional(),
      children: z.array(blockSchema).max(200).optional(),
      hidden: z.boolean().optional(),
      locked: z.boolean().optional(),
      v: z.number().int().optional(),
    })
    .strict(),
);

export const fontFamilySchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9 \-]+$/);

export const themeTokensSchema = z
  .object({
    primaryColor: hexColorSchema,
    backgroundColor: hexColorSchema,
    surfaceColor: hexColorSchema,
    textColor: hexColorSchema,
    mutedColor: hexColorSchema,
    accentColor: hexColorSchema,
    fontHeading: fontFamilySchema,
    fontBody: fontFamilySchema,
    radius: z.number().int().min(0).max(32),
    spacing: z.number().min(0.5).max(2),
    headingCase: z.enum(["none", "uppercase"]),
    priceStyle: z.enum(["inline", "dotted", "right"]),
    imageStyle: z.enum(["square", "rounded", "circle"]),
  })
  .strict();
export type ThemeTokens = z.infer<typeof themeTokensSchema>;

export const editorPageSchema = z
  .object({
    id: z.string().min(1).max(64),
    title: localizedTextSchema.optional(),
    blocks: z.array(blockSchema).max(300),
  })
  .strict();

export const editorDocumentSchema = z
  .object({
    schemaVersion: z.literal(EDITOR_SCHEMA_VERSION),
    themeId: z.string().min(1).max(64),
    themeVersion: z.number().int().optional(),
    tokens: themeTokensSchema,
    pages: z.array(editorPageSchema).min(1).max(10),
  })
  .strict();
export type EditorDocument = z.infer<typeof editorDocumentSchema>;
export type EditorPage = z.infer<typeof editorPageSchema>;

export function resolveLocalized(text: LocalizedText | string | undefined, lang: string, fallback: string): string {
  if (text == null) return "";
  if (typeof text === "string") return text;
  return text[lang] ?? text[lang.split("-")[0]!] ?? text[fallback] ?? Object.values(text)[0] ?? "";
}

/** Walk every block in document order. */
export function walkBlocks(blocks: Block[], fn: (block: Block, parent: Block | null, depth: number) => void, parent: Block | null = null, depth = 0) {
  for (const b of blocks) {
    fn(b, parent, depth);
    if (b.children) walkBlocks(b.children, fn, b, depth + 1);
  }
}

/**
 * Structural signature of a document (block ids, types and nesting) used to enforce the
 * Free plan's "basic content/style controls" editor tier on the server.
 */
export function documentStructureSignature(doc: EditorDocument): string {
  const enc = (blocks: Block[]): string => blocks.map((b) => `${b.type}:${b.id}${b.children ? `[${enc(b.children)}]` : ""}`).join(",");
  return doc.pages.map((p) => `${p.id}{${enc(p.blocks)}}`).join("|");
}
